import { Button, Flex, Stack, Td, Text } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { Link as RouterLink } from "react-router-dom";
import type { ContractOverviewDto, ContractReportQueryDto } from "../../autogen/bff-api";
import { useDisplayDate } from "../../common/formatting/useDisplayDate";
import { urls } from "../../urls";
import { PanelRow, PanelTable, TitleCell } from "../home/panels/PanelTable";
import { URGENCY_COLOR, deadlineUrgency } from "../home/deadlineUrgency";
import { registerLink } from "./contractFigures";

export const UpcomingOptionDeadlines = ({
  overview,
  base,
}: {
  overview: ContractOverviewDto;
  base: ContractReportQueryDto;
}) => {
  const { t } = useTranslation();
  const { rows, total, undated } = overview.upcomingOptionDeadlines;
  return (
    <Stack spacing="3" borderWidth="1px" borderColor="smBorder" borderRadius="lg" p="4" bg="smBackground">
      <Flex justify="space-between" align="baseline" gap="3" wrap="wrap">
        <Text as="h2" fontWeight="semibold" color="smText">
          {t("Upcoming option deadlines")}
        </Text>
        {total > rows.length && (
          <Button
            as={RouterLink}
            to={registerLink(base, overview.upcomingOptionDeadlines)}
            size="sm"
            variant="link"
            colorScheme="teal"
          >
            {t("See all {{count}}", { count: total })}
          </Button>
        )}
      </Flex>
      {rows.length === 0 ? (
        <Text fontSize="sm" color="smMuted">
          {t("No contract has an option deadline ahead.")}
        </Text>
      ) : (
        <Flex overflowX="auto">
          <PanelTable
            headers={[t("Contract"), t("Supplier"), t("Responsible"), t("Deadline"), t("Days left")].map(String)}
          >
            {rows.map((row) => (
              <PanelRow key={row.contractId} to={urls.app.contracts.view.go(row.contractId)}>
                <TitleCell title={row.title || String(t("Untitled"))} />
                <Td>{row.supplierName ?? "–"}</Td>
                <Td>{row.responsibleName ?? "–"}</Td>
                {row.deadline == null || row.daysLeft == null ? (
                  <>
                    <Td color="smMuted" data-testid="option-deadline-not-set">
                      {t("No deadline set")}
                    </Td>
                    <Td isNumeric data-testid="option-days-left">
                      {"–"}
                    </Td>
                  </>
                ) : (
                  <DeadlineCells deadline={row.deadline} daysLeft={row.daysLeft} />
                )}
              </PanelRow>
            ))}
          </PanelTable>
        </Flex>
      )}
      {undated.hidden > 0 && (
        <Button
          as={RouterLink}
          to={registerLink(base, undated)}
          size="sm"
          variant="link"
          colorScheme="teal"
          alignSelf="flex-start"
        >
          {t("+ {{count}} without a deadline", { count: undated.hidden })}
        </Button>
      )}
    </Stack>
  );
};

const DeadlineCells = ({ deadline, daysLeft }: { deadline: string; daysLeft: number }) => {
  const { t } = useTranslation();
  const displayDate = useDisplayDate();
  const passed = daysLeft < 0;
  const urgency = passed ? "overdue" : deadlineUrgency(deadline);
  return (
    <>
      <Td color={passed ? URGENCY_COLOR.overdue : undefined}>
        {displayDate(deadline)}
        {passed && (
          <Text
            fontSize="xs"
            fontWeight="semibold"
            color="smError"
            whiteSpace="normal"
            data-testid="option-deadline-passed"
          >
            {t("The deadline passed {{count}} days ago", { count: -daysLeft })}
          </Text>
        )}
      </Td>
      <Td isNumeric color={URGENCY_COLOR[urgency]} data-urgency={urgency} data-testid="option-days-left">
        {passed ? "–" : daysLeft}
      </Td>
    </>
  );
};
